import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  GetCalendarServiceParams,
  GetCalendarServiceResponse,
  GetEventsQueryParams,
  GetEventsResponse,
  CreateEventPayload,
  CreateEventResponse,
  AddSessionPayload,
  AddSessionResponse,
  PublishEventPayload,
  PublishEventResponse,
  GetEventResponse,
  AddPresentationToEventPayload,
  AddPresentationToEventResponse,
  AddLineupPayload,
  AddLineupResponse,
  GetEventReviewsParams,
  GetEventReviewsResponse,
  GetAgendaTypesResponse,
  GetAudioAllowed,
  GetEventPlaybooksResponse,
  GetEventPlaybookSelectionResponse,
  UpdateEventPlaybooksPayload,
  UpdateEventPlaybooksResponse,
} from "interfaces";
import {
  getCalendar,
  getEvents,
  createEventService,
  addSessionService,
  publishEventService,
  getEventService,
  addPresentationToEventService,
  addLineupService,
  getEventReviews,
  getAgendaTypes,
  questionAllowed,
  getEventPlaybooksService,
  getEventPlaybookSelectionService,
  updateEventPlaybooksService,
} from "services/events.service";

// // Query Hooks (GET requests)
export const useGetCalendar = (params: GetCalendarServiceParams) => {
  return useQuery<GetCalendarServiceResponse>({
    queryKey: ["get-calendar", ...Object.values(params)],
    queryFn: () => getCalendar(params),
  });
};

export const useGetEvents = (
  params: GetEventsQueryParams,
  enabled: boolean = true,
) => {
  return useQuery<GetEventsResponse>({
    queryKey: ["get-events", ...Object.values(params)],
    queryFn: () => getEvents(params),
    enabled,
  });
};

export const useCreateEvent = () => {
  return useMutation<CreateEventResponse, Error, CreateEventPayload>({
    mutationFn: (payload: CreateEventPayload) => createEventService(payload),
  });
};

export const useAddSession = () => {
  return useMutation<AddSessionResponse, Error, FormData>({
    mutationFn: (payload: FormData) => addSessionService(payload),
  });
};

export const usePublishEvent = () => {
  return useMutation<PublishEventResponse, Error, PublishEventPayload>({
    mutationFn: (payload: PublishEventPayload) => publishEventService(payload),
  });
};

export const useGetEvent = (eventId: string, enabled: boolean = true) => {
  return useQuery<GetEventResponse>({
    queryKey: ["event", eventId],
    queryFn: () => getEventService(eventId),
    enabled,
  });
};

export const useAddPresentationToEvent = () => {
  return useMutation<
    AddPresentationToEventResponse,
    Error,
    AddPresentationToEventPayload
  >({
    mutationFn: (payload: AddPresentationToEventPayload) =>
      addPresentationToEventService(payload),
  });
};

export const useAddLineup = () => {
  return useMutation<AddLineupResponse, Error, AddLineupPayload>({
    mutationFn: (payload: AddLineupPayload) => addLineupService(payload),
  });
};

export const useGetEventReviews = (params: GetEventReviewsParams) => {
  return useQuery<GetEventReviewsResponse>({
    queryKey: ["get-event-reviews", ...(params ? Object.values(params) : [])],
    queryFn: () => getEventReviews(params),
  });
};

export const useGetAgendaTypes = () => {
  return useQuery<GetAgendaTypesResponse>({
    queryKey: ["get-agenda-types"],
    queryFn: () => getAgendaTypes(),
  });
};

export const eventPlaybookQueryKeys = {
  catalogue: ["event-playbooks"],
  selection: (eventId: string) => ["event-playbooks", "selection", eventId],
};

export const useGetEventPlaybooks = (enabled: boolean = true) => {
  return useQuery<GetEventPlaybooksResponse>({
    queryKey: eventPlaybookQueryKeys.catalogue,
    queryFn: () => getEventPlaybooksService(),
    enabled,
  });
};

export const useGetEventPlaybookSelection = (
  eventId: string,
  enabled: boolean = true,
) => {
  return useQuery<GetEventPlaybookSelectionResponse>({
    queryKey: eventPlaybookQueryKeys.selection(eventId),
    queryFn: () => getEventPlaybookSelectionService(eventId),
    enabled: enabled && Boolean(eventId),
  });
};

export const useUpdateEventPlaybooks = () => {
  const queryClient = useQueryClient();

  return useMutation<
    UpdateEventPlaybooksResponse,
    Error,
    UpdateEventPlaybooksPayload
  >({
    mutationFn: (payload: UpdateEventPlaybooksPayload) =>
      updateEventPlaybooksService(payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: eventPlaybookQueryKeys.selection(variables.eventId),
      });
      queryClient.invalidateQueries({ queryKey: ["automations"] });
    },
  });
};

export const useAllowsAudio = (payload: GetAudioAllowed) => {
  return useQuery({
    queryFn: () => questionAllowed(payload),
    queryKey: ["question-allowed", ...Object.values(payload)],
    enabled:
      !!payload.eventId &&
      !!payload.presentationId &&
      !!payload.presentaConnectionId,
  });
};
